
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Archive } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { serviceFactory } from '@/services/core/serviceFactory';
import { GroupsService } from '@/services/features/GroupsService';
import PaginationComponent from '@/components/common/PaginationComponent';
import LoaderComponent from '@/components/common/LoaderComponent';

interface ArchivedGroup {
  group_id: number;
  group_name: string;
  es_id: number[];
  es_name: string;
  group_type: string;
  group_type_id: number;
  clinic_id: number;
  clinic_name: string;
  updated_time_utc: string;
  patient_clinic_timezone: string;
  is_archive: boolean;
}

interface ArchivedGroupsProps {
  onBack: () => void;
}

const ArchivedGroups = ({ onBack }: ArchivedGroupsProps) => {
  const { hasuraToken, user, token } = useAuth();
  const { toast } = useToast();
  const [groups, setGroups] = useState<ArchivedGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ArchivedGroup | null>(null);

  // Initialize service
  const groupsService = serviceFactory.getService(GroupsService);

  // Update service context when auth changes
  useEffect(() => {
    if (user && token && hasuraToken) {
      groupsService.updateContext({
        userId: user.id,
        token: token,
        hasuraToken: hasuraToken,
        userType: user.type
      });
    }
  }, [user, token, hasuraToken, groupsService]);

  const fetchArchivedGroups = async () => {
    if (!hasuraToken) return;

    setIsLoading(true);

    // Add 1 second delay before showing loader and loading records
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      const result = await groupsService.fetchArchivedGroups();

      if (result.success && result.data?.pep_vw_group_details) {
        // Map API GroupDetails to UI ArchivedGroup format
        const mappedGroups = result.data.pep_vw_group_details.map((group: any) => ({
          ...group,
          es_id: [group.es_id], // Convert single number to array for UI compatibility
        }));
        setGroups(mappedGroups);
      } else {
        throw new Error(result.errors?.[0] || 'Failed to fetch archived groups');
      }
    } catch (err) {
      console.error('Error fetching archived groups:', err);
      toast({
        title: "Fetch Failed",
        description: err instanceof Error ? err.message : "Failed to fetch archived groups. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchive = async (groupId: number) => {
    if (!user?.id) {
      toast({
        title: "Authentication Error",
        description: "User ID not found",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await groupsService.unarchiveGroup(groupId);

      if (result.success) {
        toast({
          title: "Success",
          description: "Group unarchived successfully",
        });
        setShowUnarchiveDialog(false);
        setSelectedGroup(null);
        // Remove the unarchived group from the list
        setGroups(prevGroups => prevGroups.filter(group => group.group_id !== groupId));
        // Reload the records after unarchiving
        await fetchArchivedGroups();
      } else {
        throw new Error(result.message || 'Failed to unarchive group');
      }
    } catch (err) {
      console.error('Error unarchiving group:', err);
      toast({
        title: "Unarchive Failed",
        description: err instanceof Error ? err.message : "Failed to unarchive group. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchiveClick = (group: ArchivedGroup) => {
    setSelectedGroup(group);
    setShowUnarchiveDialog(true);
  };

  useEffect(() => {
    fetchArchivedGroups();
  }, [hasuraToken]);

  const totalPages = Math.ceil(groups.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedGroups = groups.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-gradient-to-br from-purple-100 to-pink-100">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45">
          <div className="text-6xl font-bold text-gray-400">HBOX ARCHIVE</div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-purple-100 border-b border-purple-200 relative z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              className="text-purple-600 hover:text-purple-900 hover:bg-purple-200"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-purple-300" />
            <img
              src="/lovable-uploads/6823d161-b5be-421b-863a-12eaf2ea796e.png"
              alt="HBox Logo"
              className="h-8 w-auto"
            />
            <div className="text-xl font-semibold text-purple-900">
              <div>PEP</div>
              <div>VPE Admin Dashboard - Archived Groups</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6 relative z-10">
        <Card className="bg-white/95 backdrop-blur-sm shadow-xl border-0">
          <CardContent className="p-0">
            {isLoading ? (
              <LoaderComponent
                size="lg"
                text="Loading archived groups..."
                className="h-32"
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-purple-100/50">
                      <TableRow>
                        <TableHead className="text-gray-700 font-semibold">Group ID</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Group Name</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Type</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Assigned VPE</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Clinic Name</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedGroups.map((group) => (
                        <TableRow key={group.group_id} className="hover:bg-purple-50/50">
                          <TableCell className="text-gray-900 font-medium">{group.group_id}</TableCell>
                          <TableCell className="text-gray-700">{group.group_name}</TableCell>
                          <TableCell className="text-gray-700">{group.group_type}</TableCell>
                          <TableCell className="text-gray-700">{group.es_name || 'Not assigned'}</TableCell>
                          <TableCell className="text-gray-700">{group.clinic_name}</TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleUnarchiveClick(group)}
                              size="sm"
                              className="bg-purple-500 hover:bg-purple-600 text-white rounded-full w-10 h-10 p-0"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {groups.length === 0 && (
                  <div className="text-center py-12">
                    <Archive className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No archived groups found</h3>
                    <p className="text-gray-500">All groups are currently active.</p>
                  </div>
                )}

                {/* Pagination */}
                {groups.length > 0 && (
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    recordsPerPage={rowsPerPage}
                    totalRecords={groups.length}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onPageChange={handlePageChange}
                    onRecordsPerPageChange={handleRowsPerPageChange}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Unarchive Confirmation Dialog */}
      <AlertDialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unarchive Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unarchive "{selectedGroup?.group_name}"? This will move it back to the active groups list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedGroup && handleUnarchive(selectedGroup.group_id)}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Unarchive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivedGroups;
